import { Flex, Box, Text, Card, Image, Badge, Modal, ActionIcon, createStyles } from '@mantine/core';
import { Carousel } from '@mantine/carousel';
import { useEffect, useState } from 'react';
import { useRequest } from 'ahooks';
import { useStore } from '../../../hooks/use-store';
import { PipelineData } from './Pipeline';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import ImageObserved from './Image';
import { useHistory } from 'react-router-dom';

const useStyles = createStyles((theme) => ({
  card: {
    position: 'relative',
    overflow: 'hidden',
    marginBottom: '1.5rem',
    display: 'inline-block',
    width: '100%',
    breakInside: 'avoid',
    cursor: 'pointer',
    '&:hover .overlay': {
      opacity: 1,
    },
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%)',
    opacity: 0,
    transition: 'opacity 0.2s ease',
    display: 'flex',
    alignItems: 'flex-end',
    padding: '1rem',
  },
  promptText: {
    color: 'white',
    maxHeight: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
  },
}));

const heroSlides = [
  {
    image: '/rayu2ol97qv8nzjllanzknh3fr0ak2.jpg',
    label: 'START NOW',
    title: 'Start now: Generate AI Images',
    description: 'Select a location and choose the AI-Workflow you want to use.',
    button: { label: 'Restyle Videos', color: 'dark', variant: 'white' },
  },  
  {
    image: '/3DCamera.jpg',
    label: 'NEW TOOL',
    title: '3D Camera in MA-AI',
    description: 'Introducing the 3D Camera in MA-AI: Total Control for Automotive Visuals.',
    button: { label: 'Restyle Videos', color: 'dark', variant: 'white' },
  },
  {
    image: 'https://maground.com/Maground/image-store/teasers/storage/c/d/f/9/cbee59a1d547b4def817d70893c01354.jpg',
    label: 'NEW TOOL',
    title: 'Introducing 360 + HDR from Image',
    description: 'Generate 360° HDR-Domes for lighting and reflections from a single image.',
    button: { label: 'Try 3D Objects', color: 'dark', variant: 'white' },
  }, {
    image: '/LocationLorA_bright.png',
    label: 'NEW TOOL',
    title: 'Introducing Location Lora Training',
    description: 'Train location-specific LoRAs using the full MAGROUND image archive.',
    button: { label: 'Try 3D Objects', color: 'dark', variant: 'white' },
  }, {
    image: '/AIRetouch.png',
    label: 'NEW TOOL',
    title: 'Introducing Brand New AI Retouch',
    description: ' Upgrade your photos with professional-grade AI-based retouching tools.',
    button: { label: 'Try 3D Objects', color: 'dark', variant: 'white' },
  }, {
    image: 'https://maground.com/Maground/image-store/teasers/storage/9/4/7/7/97075e274b1bf4187e29b3ef7999be26.jpg',
    label: 'NEW TOOL',
    title: 'New: UI Restyle',
    description: 'MA-AI now recieve visual update, based on your feedback.',
    button: { label: 'Try 3D Objects', color: 'dark', variant: 'white' },
  },
];


const Examples = () => {
  const { classes } = useStyles();


  const history = useHistory();
    
  const {
    dataStore: { appStore },
  } = useStore();

  const [featured, setFeatured] = useState<{
    record_id: number;
    data: PipelineData;
  }[]>([]);

  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [allImages, setAllImages] = useState<Array<{
    url: string;
    imgIdx: number;
    item: any;
    idx: number;
  }>>([]);

  const shuffleArray = <T,>(array: T[]): T[] => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  const { runAsync: getFeaturedData } = useRequest(
    async () =>
      appStore.getFeaturedData(),
    {
      loadingDelay: 20,
      manual: true,
      onSuccess: (model) => { },
    }
  );

  useEffect(() => {
    async function fetchFeatured() {
      const res: {
        record_id: number
        data: PipelineData
      }[] = await getFeaturedData();
      setFeatured(res);
    }
    fetchFeatured();
  }, [getFeaturedData]);

  useEffect(() => {
    const images = featured.flatMap((item, idx) => {
      const getImageUrls = (params: PipelineData['params']) => {
        if ('srcset' in params && Array.isArray(params.srcset) && params.srcset.length > 0) {
          return params.srcset;
        }
        if ('outputSrc' in params && typeof params.outputSrc === 'string') {
          return [params.outputSrc];
        }
        if ('inputSrc' in params && typeof params.inputSrc === 'string') {
          return [params.inputSrc];
        }
        return ['https://images.unsplash.com/photo-1502082553048-f009c37129b9?auto=format&fit=crop&w=400&q=80'];
      };

      const imageUrls = getImageUrls(item.data.params);

      return imageUrls.map((url, imgIdx) => ({
        url,
        imgIdx,
        item,
        idx
      }));
    });
    setAllImages(shuffleArray(images));
  }, [featured]);

  const handleImageClick = (index: number) => {
    setPreviewIndex(index);
  };

  const handlePrev = () => {
    if (previewIndex === null) return;
    setPreviewIndex((prevIndex) => {
      if (prevIndex === null) return 0;
      return prevIndex === 0 ? allImages.length - 1 : prevIndex - 1;
    });
  };

  const handleNext = () => {
    if (previewIndex === null) return;
    setPreviewIndex((prevIndex) => {
      if (prevIndex === null) return 0;
      return prevIndex === allImages.length - 1 ? 0 : prevIndex + 1;
    });
  };

  return (
    <Flex direction="column" gap={32} style={{ background: '#111', minHeight: '100vh' }} mt={40}>
      {/* Hero Carousel Section - Stick View */}
      <Box style={{ width: '100%', maxWidth: 1800, margin: '0 auto', position: 'relative', height: 420 }}>
        <Carousel
          withIndicators
          height={420}
          slideSize="48%"
          slideGap="xl"
          align="start"
          slidesToScroll={1}
          loop
          draggable
          styles={{ control: { background: '#222', color: '#fff' } }}
        >
          {heroSlides.map((slide, idx) => (
            <Carousel.Slide key={idx}>
              <Box 
                onClick={(e) => {
                  history.push('/generate');
                  e.stopPropagation();
                  // Add your click handler here if needed
                }}
                style={{ cursor: 'pointer' }}
              >
                <Card radius="xl" p={0} style={{ width: '100%', height: 420, overflow: 'hidden', position: 'relative', borderRadius: 24 }}>
                  <Image src={slide.image} height={420} width={840} fit="cover" alt={slide.title} />
                  <Box style={{ 
                    position: 'absolute', 
                    bottom: 0, 
                    left: 0, 
                    right: 0, 
                    height: '60%', 
                    background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%)',
                    zIndex: 1
                  }} />
                  <Box style={{ position: 'absolute', bottom: 24, left: 32, zIndex: 2, color: 'white', maxWidth: 700 }}>
                    <Text fz={16} fw={500} mb={12} style={{ letterSpacing: 1, opacity: 0.8 }}>{slide.label}</Text>
                    <Text fw={900} fz={38} mb={12} style={{ lineHeight: 1.1 }}>{slide.title}</Text>
                    <Text fz={18} mb={28} style={{ opacity: 0.95 }}>{slide.description}</Text>
                  </Box>
                </Card>
              </Box>
            </Carousel.Slide>
          ))}
        </Carousel>
      </Box>

      <Box>
        <Text fw={700} fz={20} mb={16} color="#fff">Gallery</Text>
        <Box style={{
          columnCount: 5,
          columnGap: '1.5rem',
          width: '100%',
        }}>
          {allImages.map(({ url, imgIdx, item, idx }, index) => (
            <Card
              key={`${url}-${item.data.params.prompt}`}
              radius="md"
              p={0}
              className={classes.card}
              onClick={() => handleImageClick(index)}
            >
              <ImageObserved
                src={url}
                width="100%"
                style={{ width: '100%', height: '100%' }}
                imgStyle={{ width: '100%', height: '100%' }}
                height={`${180 + (idx % 3) * 40}px`}
                objectFit="cover"
              />
              <Box className={`${classes.overlay} overlay`}>
                <Text size="sm" className={classes.promptText}>
                  {item.data.params.prompt || 'No prompt available'}
                </Text>
              </Box>
              {imgIdx === 0 && item.data.params.type === 'gen360' && (
                <Badge color="orange" style={{ position: 'absolute', top: 12, left: 12 }}>360°</Badge>
              )}
            </Card>
          ))}
        </Box>
      </Box>

      <Modal
        opened={previewIndex !== null}
        onClose={() => setPreviewIndex(null)}
        size="xl"
        centered
        withCloseButton={false}
        padding={0}
        styles={{
          body: { padding: 0 },
          content: { background: 'transparent' },
        }}
      >
        {previewIndex !== null && (
          <Box style={{ position: 'relative', height: '80vh' }}>
            <ImageObserved
              src={allImages[previewIndex].url}
              width="100%"
              height="100%"
              objectFit="contain"
            />
            <ActionIcon
              size="xl"
              radius="xl"
              variant="filled"
              color="dark"
              style={{
                position: 'absolute',
                left: 20,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 2,
              }}
              onClick={handlePrev}
            >
              <IconChevronLeft size={24} />
            </ActionIcon>
            <ActionIcon
              size="xl"
              radius="xl"
              variant="filled"
              color="dark"
              style={{
                position: 'absolute',
                right: 20,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 2,
              }}
              onClick={handleNext}
            >
              <IconChevronRight size={24} />
            </ActionIcon>
          </Box>
        )}
      </Modal>
    </Flex>
  );
};

export default Examples; 